import React, { useState } from 'react';
import { Card, Table, Modal, Button } from '../../../components/common';
import { apiError } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { monthlyClosingService } from '../../../services/resources';

const fmt = (n) =>
  n === null || n === undefined || n === '' ? '' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 });

function currentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

// The system went live in July 2026 — no period before that has real transaction data.
const EARLIEST_PERIOD = '2026-07';

// Auto-formats raw digits into YYYY-MM as the user types (mirrors DateInput's masking).
function maskPeriod(input) {
  const digits = String(input).replace(/\D/g, '').slice(0, 6);
  const y = digits.slice(0, 4);
  const m = digits.slice(4, 6);
  return digits.length >= 5 ? `${y}-${m}` : digits.length === 4 ? `${y}-` : y;
}

export default function MonthlyClosingPage({ onToast }) {
  const { can } = useAuth();
  const canView = can('MONTHLY_CLOSING', 'CanView');
  const canCreate = can('MONTHLY_CLOSING', 'CanCreate');

  const [period, setPeriod] = useState(currentPeriod());
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [previewedPeriod, setPreviewedPeriod] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState('');

  const runPreview = async () => {
    setError('');
    setLoading(true);
    try {
      const data = await monthlyClosingService.preview(period);
      setResult(data);
      setPreviewedPeriod(period);
    } catch (err) {
      setResult(null);
      setPreviewedPeriod(null);
      setError(apiError(err).message);
    } finally {
      setLoading(false);
    }
  };

  const doClose = async () => {
    setClosing(true);
    try {
      const data = await monthlyClosingService.close(period);
      setResult(data);
      setConfirmOpen(false);
      onToast?.(data.isReclose ? 'Period re-closed.' : 'Period closed.', 'success');
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    } finally {
      setClosing(false);
    }
  };

  if (!canView) {
    return (
      <Card title="Monthly Closing">
        <p className="text-sm text-gray-500">You do not have permission to view this page.</p>
      </Card>
    );
  }

  const columns = [
    { key: 'currency_code', label: 'Currency' },
    { key: 'opening_stock_qty', label: 'Opening Qty', render: (r) => fmt(r.opening_stock_qty) },
    { key: 'period_buy_qty', label: 'Buy Qty', render: (r) => fmt(r.period_buy_qty) },
    { key: 'period_sell_qty', label: 'Sell Qty', render: (r) => fmt(r.period_sell_qty) },
    {
      key: 'weighted_avg_cost',
      label: 'Weighted Avg Cost',
      render: (r) => (r.weighted_avg_cost === null ? '—' : fmt(r.weighted_avg_cost)),
    },
    {
      key: 'ending_stock_qty',
      label: 'Ending Qty',
      render: (r) => (
        <span className={r.negative_stock_warning ? 'font-semibold text-red-600' : ''}>
          {fmt(r.ending_stock_qty)}
        </span>
      ),
    },
    {
      key: 'warning',
      label: 'Warning',
      render: (r) =>
        r.negative_stock_warning ? (
          <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">Negative stock</span>
        ) : r.zero_cost_basis_warning ? (
          <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">Zero cost basis</span>
        ) : null,
    },
  ];

  const alreadyClosed = result?.isReclose;
  const periodValid = /^\d{4}-\d{2}$/.test(period) && period >= EARLIEST_PERIOD;
  const canClose = canCreate && previewedPeriod === period && periodValid;

  return (
    <div className="space-y-4">
      <Card title="Monthly Closing">
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Period</span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="YYYY-MM"
              value={period}
              onChange={(e) => {
                setPeriod(maskPeriod(e.target.value));
                setPreviewedPeriod(null);
                setResult(null);
              }}
              className="w-32 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </label>
          <Button onClick={runPreview} disabled={loading || !periodValid}>
            {loading ? 'Computing…' : 'Preview'}
          </Button>
          <Button
            variant={alreadyClosed ? 'secondary' : 'primary'}
            onClick={() => setConfirmOpen(true)}
            disabled={!canClose}
          >
            {alreadyClosed ? 'Re-close Period' : 'Close Period'}
          </Button>
        </div>
        {/^\d{4}-\d{2}$/.test(period) && !periodValid && (
          <p className="mt-3 text-sm text-amber-600">
            Periods before {EARLIEST_PERIOD} cannot be closed (system start date).
          </p>
        )}
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </Card>

      {result && (
        <Card title={`Preview — ${result.period}${result.isReclose ? ' (already closed)' : ''}`}>
          {result.warnings.length > 0 && (
            <ul className="mb-3 space-y-1 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
              {result.warnings.map((w, i) => (
                <li key={i}>⚠ {w}</li>
              ))}
            </ul>
          )}
          <Table columns={columns} rows={result.currencies} loading={false} emptyText="No tradable currencies." />
        </Card>
      )}

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={alreadyClosed ? 'Re-close Period' : 'Close Period'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)} disabled={closing}>
              Cancel
            </Button>
            <Button variant={alreadyClosed ? 'danger' : 'primary'} onClick={doClose} disabled={closing}>
              {closing ? 'Closing…' : alreadyClosed ? 'Re-close' : 'Close'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600">
          {alreadyClosed
            ? `This period is already closed. Re-closing will overwrite previously stamped COGS values for ${period} based on current transaction data.`
            : `This will stamp COGS on all SELL transactions in ${period} and lock in the weighted-average cost for this period.`}
        </p>
      </Modal>
    </div>
  );
}
